import React from 'react';
import { 
  Building2, 
  ChevronRight, 
  Layers, 
  MapPin, 
  Sparkles, 
  CheckCircle2, 
  ArrowRight,
  BookOpen
} from 'lucide-react';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';

interface ExamCatalogOverviewProps {
  onSelectExam: (entry: ExamCatalogEntry) => void;
  onViewAllIndividualPapers?: () => void;
}

export const ExamCatalogOverview: React.FC<ExamCatalogOverviewProps> = ({
  onSelectExam,
  onViewAllIndividualPapers
}) => {
  return (
    <div className="space-y-8 max-w-7xl mx-auto py-2">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white rounded-2xl p-6 sm:p-8 shadow-xl border border-indigo-900/40 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-72 h-72 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2 text-xs font-semibold text-indigo-300">
              <span className="px-2.5 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30">
                State Certification Catalog
              </span>
              <span className="text-amber-300 font-bold flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> {examCatalog.length} Exam Systems Supported
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Select Your State Certification Exam
            </h1>
            <p className="text-slate-300 text-sm leading-relaxed">
              Choose your target state below to access state-specific passing standards, sub-exam practice tests, and AI-powered study tools.
            </p>
          </div>

          {onViewAllIndividualPapers && (
            <div className="shrink-0">
              <button
                onClick={onViewAllIndividualPapers}
                className="bg-white/10 hover:bg-white/20 text-white font-bold text-xs px-4 py-2.5 rounded-xl border border-white/20 transition flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4 text-indigo-300" />
                <span>View All Individual Papers</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Grid of Exam Catalog Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {examCatalog.map((entry) => (
          <div
            key={entry.id}
            onClick={() => onSelectExam(entry)}
            className="group cursor-pointer bg-white rounded-2xl border border-slate-200 shadow-xs hover:shadow-lg hover:border-indigo-400 transition-all duration-200 flex flex-col justify-between p-6 space-y-4 relative overflow-hidden"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 group-hover:bg-indigo-600 text-indigo-700 group-hover:text-white font-black text-sm flex items-center justify-center transition-colors">
                  {(entry.state || 'EX').slice(0, 2).toUpperCase()}
                </div>
                <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100">
                  {entry.examCodes.length} Sub-Exams
                </span>
              </div>

              <div>
                <h3 className="text-lg font-extrabold text-slate-900 group-hover:text-indigo-600 transition-colors">
                  {entry.state}
                </h3>
                <p className="text-xs text-slate-500 font-medium leading-relaxed mt-1 line-clamp-2">
                  {entry.examLabel}
                </p>
              </div>

              {/* Sub-exam code pills */}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {entry.examCodes.map((code) => (
                  <span
                    key={code}
                    className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 group-hover:bg-indigo-50 group-hover:text-indigo-800 transition-colors"
                  >
                    {code}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA Footer */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-indigo-600 group-hover:text-indigo-700">
              <span>View exams</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
